import { useId } from "react";
import type { FieldPath, FieldValues, UseControllerProps } from "react-hook-form";
import { useController } from "react-hook-form";

import type { DatePickerProps } from "@/components/DatePicker/DatePicker";
import { DatePicker } from "@/components/DatePicker/DatePicker";

import { FormErrorWrapper } from "./FormErrorWrapper";

type Props<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> = UseControllerProps<TFormValues, TName> &
  Omit<DatePickerProps, "defaultValue" | "name" | "value" | "onChange"> & {
    type?: "date" | "datetime" | "time";
    onChange?: (value: Date | undefined) => void;
  };

export function FormDatePicker<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  defaultValue,
  onChange: propOnChange,
  name,
  control,
  rules,
  shouldUnregister,
  "data-testid": dataTestId,
  "aria-describedby": describedBy,
  ...props
}: Props<TFormValues, TName>): React.ReactNode {
  const errorId = useId();
  const {
    field: { onChange: fieldOnChange, ...field },
    fieldState,
  } = useController({
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  });
  const isError = fieldState.error != null;
  const errorMessage = fieldState.error?.message;
  const descriptionIds =
    [describedBy, errorMessage != null ? errorId : undefined].filter(Boolean).join(" ") || undefined;

  return (
    <FormErrorWrapper name={name} errorId={errorId}>
      <DatePicker
        data-testid={dataTestId}
        {...props}
        {...field}
        onChange={(date: Date | undefined) => {
          fieldOnChange(date);
          propOnChange?.(date);
        }}
        error={isError}
        aria-invalid={isError}
        aria-describedby={descriptionIds}
      />
    </FormErrorWrapper>
  );
}
